import { useRouter } from 'next/router'
import styled, { useTheme } from 'styled-components'
import FlatIcon from '@/components/common/icons/FlatIcon'
import { NewProductCard } from '@/components/domains/products/new-product-card/new-product-card'
import { PRODUCT_CARD_SIZES } from '@/components/domains/products/new-product-card/new-product-card.type'
import SkeletonProduct from '@/components/domains/products/skeleton/SkeletonProduct'
import { IconNameEnum } from '@/constants/icon-name.enum'
import { ProductThumbnailSizeEnum } from '@/constants/product-thumbnail-size.enum'
import { EventContainer } from '@/containers/contexts/regional/event.container'
import useEventDetailSection from '@/containers/events/event-detail-section.hook'
import { ProductTypeDefinedByFrontend } from '@/types/product-type-defined-by.frontend'
import { transformProductTypeDefinedByFrontend } from '@/utils/product/product-card.util'

interface EventDetailSectionProps {
  sectionIndex: number
  displayCollectionId: string
  displayCollectionTabName: string
}

const skeletonDefaultLength = 20 // 20은 임시값

const EventDetailSection = ({
  sectionIndex,
  displayCollectionId: id,
  displayCollectionTabName: sectionTabName,
}: EventDetailSectionProps) => {
  const { iconSize, color } = useTheme()
  const { ad } = EventContainer.useContainer()

  const {
    selectOptionData,
    selectOptionDisplay,
    selectedOptionValue,
    totalCount,
    fetchedData,
    isLoading,
    handleSelectChange,
    trackClickEventsaleFilter,
    trackClickEventSaleProduct,
  } = useEventDetailSection({
    sectionIndex,
    id,
  })

  const skeletonLength = totalCount || skeletonDefaultLength
  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    handleSelectChange(e)
    trackClickEventsaleFilter({
      adId: ad.id,
      adName: ad.name,
      adSlug: ad.slug ?? '',
      sectionIndex,
      sectionTabName,
      sortBy: selectOptionData[e.target.value].trackerSortBy,
    })
  }

  const onClickProduct = (product: ProductTypeDefinedByFrontend) => {
    trackClickEventSaleProduct({
      adId: ad.id,
      adName: ad.name,
      adSlug: ad.slug ?? '',
      sectionIndex,
      sectionTabName,
      product,
    })
  }

  return (
    <>
      <StyledFilter>
        <div className="totalCount">{totalCount}개</div>
        <div className="select-box">
          <select value={selectedOptionValue} onChange={handleFilterChange}>
            {selectOptionDisplay.map((item) => (
              <option key={item.value} value={item.value}>
                {item.text}
              </option>
            ))}
          </select>
          <FlatIcon type={IconNameEnum.IcChevronDown} size={iconSize.size16} color={color.gray400} />
        </div>
      </StyledFilter>
      <StyledListContainer>
        {isLoading ? (
          <SkeletonProduct length={skeletonLength} size={ProductThumbnailSizeEnum.Fill} />
        ) : (
          fetchedData?.data?.products?.map((product, index) => {
            if (!product) {
              return null
            }

            const productCardData = transformProductTypeDefinedByFrontend(product)
            return (
              <NewProductCard
                key={productCardData.id}
                product={productCardData}
                size={PRODUCT_CARD_SIZES.md}
                rankingNumber={index}
                onClickProductCard={() => {
                  onClickProduct(productCardData)
                }}
              />
            )
          })
        )}
      </StyledListContainer>
    </>
  )
}

const StyledListContainer = styled.div`
  display: grid;
  grid-auto-flow: row;
  grid-template-columns: repeat(3, 1fr);
  grid-column-gap: 0.8rem;
  grid-row-gap: 2.4rem;
`

const StyledFilter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.6rem;

  .totalCount {
    font-size: 1.4rem;
    font-weight: 400;
    line-height: 1.8rem;
    color: ${(props) => props.theme.color.gray500};
  }

  .select-box {
    display: flex;
    align-items: center;

    select {
      margin-right: 0.4rem;
      border: none;
      cursor: pointer;
      background-color: ${(props) => props.theme.color.grayWhite};
      color: ${(props) => props.theme.color.gray500};
      font-size: ${(props) => props.theme.fontSize.body3};
      font-weight: ${(props) => props.theme.fontWeight.regular};
      direction: rtl;

      /* select의 기본 화살표를 표시하지 않음 */
      /* for Firefox */
      -moz-appearance: none;
      /* for Chrome */
      -webkit-appearance: none;
    }
  }
`

export default EventDetailSection
